import type { AssignableRequest, CallsignRow } from '@/types/timeline'

import { DAY_MINUTES, parseTimeToMinutes } from './time'

export type AssignmentValidationError = 'outsideWorkingHours' | 'noStaffAssigned'
export type AssignmentValidationMessageParams = {
  requestStartTime: string
  requestEndTime: string
  callsignStartTime: string
  callsignEndTime: string
}

export const isCallsignOnShift = (callsign: CallsignRow, timeMinutes: number): boolean => {
  const shiftStart = parseTimeToMinutes(callsign.shiftStartTime)
  const shiftEnd = parseTimeToMinutes(callsign.shiftEndTime)

  if (shiftStart == null || shiftEnd == null || shiftStart === shiftEnd) return true

  const endsNextDay = callsign.endsNextDay || shiftEnd < shiftStart
  return endsNextDay
    ? timeMinutes >= shiftStart || timeMinutes < shiftEnd
    : timeMinutes >= shiftStart && timeMinutes < shiftEnd
}

export const isRequestWithinCallsignHours = (
  request: AssignableRequest,
  callsign: CallsignRow
): boolean => {
  let requestStart = parseTimeToMinutes(request.startTime)
  let requestEnd = parseTimeToMinutes(request.endTime)
  const shiftStart = parseTimeToMinutes(callsign.shiftStartTime)
  let shiftEnd = parseTimeToMinutes(callsign.shiftEndTime)

  if (requestStart == null || requestEnd == null || shiftStart == null || shiftEnd == null) {
    return true
  }

  if (requestEnd < requestStart) requestEnd += DAY_MINUTES

  const endsNextDay = callsign.endsNextDay || shiftEnd < shiftStart
  if (endsNextDay) {
    shiftEnd += DAY_MINUTES
    if (requestStart < shiftStart) {
      requestStart += DAY_MINUTES
      requestEnd += DAY_MINUTES
    }
  }

  return requestStart >= shiftStart && requestEnd <= shiftEnd
}

export const getAssignmentValidationError = (
  request: AssignableRequest,
  callsign: CallsignRow
): AssignmentValidationError | null => {
  if (!callsign.staffName?.trim()) {
    return 'noStaffAssigned'
  }

  if (!isRequestWithinCallsignHours(request, callsign)) {
    return 'outsideWorkingHours'
  }

  return null
}

export const getAssignmentValidationMessageParams = (
  request: AssignableRequest,
  callsign: CallsignRow
): AssignmentValidationMessageParams => ({
  requestStartTime: request.startTime,
  requestEndTime: request.endTime,
  callsignStartTime: callsign.shiftStartTime,
  callsignEndTime: callsign.shiftEndTime,
})
